/** @type {import('../help-index.js').HelpSidebarItem} */
export const COS_FIC_ITEM = {
  id: 'costing_sheet',
  screenId: 'cos-fic',
  label: 'Ficha de costeo',
  description: 'Todo el costo de un artículo en una sola pantalla: por método, fijo y variable, márgenes y equilibrio.',
  detail: {
    sections: [
      {
        title: 'Paso 1 — Elegí el artículo y el mes',
        bullets: [
          'Escribí al menos 3 letras del artículo en «Artículo» y elegilo de la lista.',
          'En «Período» elegí el mes que querés analizar. Las ventas, los costos indirectos y el stock al cierre son de ese mes.',
          'La ficha se arma sola. Si cargaste compras o movimientos nuevos, tocá «Recalcular» para actualizarla.',
        ],
      },
      {
        title: 'Avisos',
        paragraphs: [
          'Si falta información (por ejemplo, salidas sin stock valuado, productos sin precio o costos indirectos sin asignar), aparece un recuadro amarillo con los avisos. Conviene revisarlos antes de tomar decisiones con los números.',
        ],
      },
      {
        title: 'Cuadros de resumen',
        fields: [
          { label: 'Costo completo', description: 'Costo directo más todos los costos indirectos (variables y fijos) por unidad, con el método predeterminado.' },
          { label: 'Costo variable', description: 'Costo directo más los indirectos variables. Es lo que cuesta producir o vender una unidad más.' },
          { label: 'Stock al cierre / Valor stock', description: 'Cantidad en stock al último día del mes y cuánto vale con FIFO y con promedio ponderado.' },
          { label: 'Última compra', description: 'Costo de la última factura de compra del artículo, en pesos.' },
          { label: 'Costo estándar', description: 'El costo objetivo que cargaste en «Costo estándar» vigente en el mes.' },
        ],
      },
      {
        title: 'Costo unitario por método',
        intro: 'Muestra el mismo artículo costeado de cuatro formas, para compararlas:',
        fields: [
          { label: 'FIFO (PEPS)', description: 'Lo primero que entró es lo primero que sale. El stock queda valuado con las compras más recientes.' },
          { label: 'Promedio ponderado (PPP)', description: 'Cada compra se mezcla con el stock que había y se saca un costo promedio.' },
          { label: 'Estándar', description: 'El costo que cargaste a mano como objetivo. Sirve también para servicios y artículos sin stock.' },
          { label: 'Reposición', description: 'Lo que costaría volver a comprarlo hoy (última compra).' },
        ],
        paragraphs: [
          'La fila resaltada es el método predeterminado (se cambia en «Configuración»). Debajo se ve el costo de ventas del mes: el costo real de las unidades que se vendieron.',
        ],
      },
      {
        title: 'Composición y fijo / variable',
        paragraphs: [
          'La barra muestra qué parte del costo completo es directo, indirecto variable e indirecto fijo. Al lado están los totales del mes asignados al artículo y su valor por unidad.',
        ],
      },
      {
        title: 'Márgenes por lista de precios',
        bullets: [
          'Una fila por cada lista de precios donde está el artículo, más el precio promedio al que realmente se vendió en el mes.',
          'Los precios se muestran sin IVA, para comparar con el costo.',
          'En «Calcular con» elegí qué método de costo usar.',
          'Margen y markup se calculan contra el costo completo. La contribución se calcula contra el costo variable: es lo que cada unidad aporta para cubrir los costos fijos.',
          'Los valores negativos se ven en rojo: el precio no cubre ese costo.',
        ],
      },
      {
        title: 'Punto de equilibrio',
        paragraphs: [
          'Cuántas unidades hay que vender en el mes para cubrir los costos fijos asignados al artículo, con la lista predeterminada. «Cobertura» compara las unidades vendidas con ese mínimo: más de 100 % quiere decir que el artículo cubrió sus fijos.',
        ],
      },
      {
        title: 'Costos indirectos asignados (ABC)',
        paragraphs: [
          'Detalle de cada pool de costos que recibió el artículo en el mes: tipo (fijo o variable), inductor usado para repartir, monto y valor por unidad. Si está vacío, primero hay que asignar el período en «Costos indirectos».',
        ],
      },
      {
        title: 'Capas de stock, evolución y costeos optativos',
        bullets: [
          'Capas FIFO: cada compra que todavía está en stock, con su cantidad y costo.',
          'Evolución: cómo cambió el costo unitario en los últimos 12 meses con cada método.',
          'Costeos optativos: si tenés módulos adicionales (por ejemplo Agro o Bodega), abajo aparecen sus costeos propios, como el costo total de una campaña.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const COS_VAL_ITEM = {
  id: 'costing_valuation',
  screenId: 'cos-val',
  label: 'Valuación de stock',
  description: 'Cuánto vale el stock al cierre de cada mes, con FIFO y con promedio ponderado.',
  detail: {
    sections: [
      {
        title: 'Antes de empezar',
        paragraphs: [
          'La valuación se calcula a partir de todos los movimientos de stock: compras, ventas, devoluciones, transferencias entre depósitos y ajustes. Las entradas que no son compras (por ejemplo, producción o ajustes con importe) también suman como costo.',
          'Si es la primera vez o cargaste movimientos con fecha anterior, tocá «Recalcular valuación».',
        ],
      },
      {
        title: 'Paso 1 — Filtrar',
        fields: [
          { label: 'Cierre del mes', description: 'El mes cuyo último día querés ver.' },
          { label: 'Buscar artículo', description: 'Código o descripción. Tocá «Buscar» o Enter.' },
          { label: 'Solo con stock', description: 'Oculta los artículos que al cierre no tenían unidades.' },
        ],
      },
      {
        title: 'Paso 2 — Leer los totales y la lista',
        bullets: [
          'Arriba: valor total del stock con FIFO, con promedio ponderado, la diferencia entre ambos y la fecha del último recálculo.',
          'En la lista: cantidad, costo unitario y total de cada método, y el costo y la fecha de la última compra.',
          'Tocá una fila para ver las capas de costo actuales del artículo (qué compras quedan en stock y a qué costo).',
          'La lista se puede descargar a Excel, CSV o PDF.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const COS_IND_ITEM = {
  id: 'costing_indirect',
  screenId: 'cos-ind',
  label: 'Costos indirectos',
  description: 'Pools de gastos (fijos o variables) y cómo se reparten entre los artículos. Sirve para costeo ABC.',
  detail: {
    sections: [
      {
        title: 'Qué es un pool',
        paragraphs: [
          'Un pool junta gastos que no son de un producto en particular: alquiler, energía, sueldos de administración, amortizaciones. Cada mes el sistema toma lo gastado y lo reparte entre los artículos.',
          'Para costeo ABC, cada pool es una actividad (por ejemplo «Preparación de pedidos») y el inductor es lo que la mueve (unidades vendidas, facturación, etc.).',
        ],
      },
      {
        title: 'Paso 1 — Crear el pool',
        fields: [
          { label: 'Nombre', description: 'Cómo lo vas a reconocer, por ejemplo «Alquiler y servicios».' },
          { label: 'Tipo', description: 'Fijo: no cambia con lo que vendés (alquiler). Variable: sube y baja con la actividad (comisiones, fletes). Define si suma al costo variable o solo al completo.' },
        ],
      },
      {
        title: 'Paso 2 — Fuentes',
        intro: 'De dónde sale la plata del pool. Podés sumar varias:',
        fields: [
          { label: 'Cuenta de gasto', description: 'Toma las facturas de compra de gastos (no de mercadería para stock) y los pagos directos imputados a esa cuenta y a sus subcuentas.' },
          { label: 'Sueldos por centro de costo', description: 'Toma los recibos de sueldo cerrados de los empleados de ese centro de costo, con cargas sociales.' },
          { label: 'Amortización de bienes de uso', description: 'Toma la amortización del mes de los bienes de esa categoría.' },
          { label: '% que va a este pool', description: 'Si una cuenta se comparte entre pools, repartí el porcentaje. Entre todos los pools no puede pasar de 100 %.' },
        ],
      },
      {
        title: 'Paso 3 — Reglas de reparto',
        fields: [
          { label: 'Inductor', description: 'La base del reparto: unidades vendidas, facturación, unidades producidas, costo directo de lo vendido o pesos manuales.' },
          { label: 'A quién se reparte', description: 'Todos los artículos, un grupo de artículos o uno solo.' },
          { label: '% del pool', description: 'Qué parte del pool reparte esta regla. Entre las reglas vigentes al mismo tiempo no pueden pasar de 100 %. Lo que no se reparte queda como «sin asignar».' },
          { label: 'Desde / Hasta', description: 'Opcional. Vigencia de la regla; vacío quiere decir sin límite.' },
        ],
      },
      {
        title: 'Paso 4 — Pesos manuales (opcional)',
        paragraphs: [
          'Si una regla usa el inductor «Pesos manuales», cargá acá un peso por artículo. Por ejemplo, si A pesa 2 y B pesa 1, A recibe el doble que B.',
        ],
      },
      {
        title: 'Paso 5 — Asignar período',
        bullets: [
          'En la pestaña «Asignar período» elegí el mes y tocá «Asignar período».',
          'Verás por pool el gasto del mes, lo asignado, lo que quedó sin asignar y a cuántos artículos llegó, con el detalle por artículo abajo.',
          'Si después cargás más gastos de ese mes, volvé a asignar: el resultado anterior se reemplaza.',
          'Un pool sin ventas (o sin la base que pide su inductor) en el mes queda sin asignar y aparece un aviso.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const COS_STD_ITEM = {
  id: 'costing_standard',
  screenId: 'cos-std',
  label: 'Costo estándar',
  description: 'Costo objetivo o presupuestado de cada artículo, con fecha de vigencia.',
  detail: {
    sections: [
      {
        title: 'Para qué sirve',
        paragraphs: [
          'Es el costo que esperás para un artículo. En la ficha de costeo aparece como un método más, para comparar el costo real contra el esperado.',
          'Para servicios y artículos que no llevan stock, junto con la última compra, es la forma de darles costo directo.',
        ],
      },
      {
        title: 'Paso 1 — Cargar un costo',
        fields: [
          { label: 'Artículo', description: 'Buscalo con al menos 3 letras.' },
          { label: 'Vigente desde', description: 'Desde qué día vale. Vale hasta que cargues otro con fecha posterior. No puede haber dos con la misma fecha para el mismo artículo.' },
          { label: 'Costo unitario', description: 'Costo por unidad, mayor a cero.' },
          { label: 'Moneda', description: 'Si es en otra moneda, se pasa a pesos con la cotización cargada en el sistema.' },
          { label: 'Detalle', description: 'Opcional, por ejemplo «Presupuesto 2027».' },
        ],
      },
      {
        title: 'Paso 2 — Editar o eliminar',
        bullets: [
          'Tocá el lápiz para cambiar fecha, costo, moneda o detalle. El artículo no se puede cambiar.',
          'Tocá el tacho para eliminarlo.',
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpSidebarItem} */
export const COS_CFG_ITEM = {
  id: 'costing_settings',
  screenId: 'cos-cfg',
  label: 'Configuración',
  description: 'Método de costeo predeterminado y parámetros generales.',
  detail: {
    sections: [
      {
        title: 'Opciones',
        fields: [
          { label: 'Método de valuación predeterminado', description: 'FIFO o promedio ponderado. Es el que la ficha marca como principal y usa para el punto de equilibrio. Los dos se calculan siempre.' },
          { label: 'Horas de trabajo por mes', description: 'Base para pasar sueldos mensuales a costo por hora (entre 1 y 744).' },
          { label: 'Costeos optativos disponibles', description: 'Los costeos que suman los módulos adicionales que tenés activos, por ejemplo Agro o Bodega.' },
        ],
      },
    ],
  },
}

/** @type {import('../help-index.js').HelpScreen} */
export const COS_HELP = {
  screenId: 'cos',
  label: 'Costeo',
  navPath: 'Costeo',
  keywords: [
    'costeo', 'costo', 'costos', 'fifo', 'peps', 'promedio ponderado', 'ppp', 'valuación', 'stock',
    'margen', 'markup', 'contribución', 'equilibrio', 'abc', 'indirectos', 'fijos', 'variables',
    'estándar', 'reposición', 'pool', 'inductor', 'amortización', 'ficha de costeo',
  ].map((k) => k.toLowerCase()),
  status: 'complete',
  summary: 'Costo de cada producto por varios métodos, costos fijos y variables, costeo ABC, márgenes y valuación de stock.',
  sidebarItems: [COS_FIC_ITEM, COS_VAL_ITEM, COS_IND_ITEM, COS_STD_ITEM, COS_CFG_ITEM],
}
